/**
 * CroTrendTile — trend tile with ECharts sparkline for CRO-06 and CRO-07.
 *
 * Renders (per UI-SPEC §"Tile anatomy — trend tile"):
 *   - UX-07 4-state machine (loading / CacheNotReady / SfUnreachable / success)
 *   - Current value at text-2xl font-bold (smaller than big-number tiles)
 *   - Two stacked delta lines: Δw (vs prior_week_value) + Δm (vs prior_month_avg_value)
 *     Using ▲/▼ glyphs per UI-SPEC icons (no lucide-react)
 *   - ECharts sparkline at h-32 (128px) × 100% width (canvas renderer for perf)
 *     Config per RESEARCH.md §6.1:
 *       - Line chart, no axes, no tooltip
 *       - Brand-blue (#2851e6) line with area fill rgba(40,81,230,0.08)
 *       - smooth: true, symbol: 'none'
 *       - Target markLine when target_value present
 *   - % to target chip (same color logic as CroTile)
 *   - RESEARCH.md §8.5 confidence=low amber audit badge
 *
 * Used for: CRO-06 (AE Meetings/Week), CRO-07 (Active Opps per AE)
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../lib/api-client.js';
import { getCroMetricDefinition } from '../lib/definitions.js';
import { formatPercentToTarget, targetChipVariant } from '../lib/fiscal.js';
import { formatCount } from '../lib/format.js';

import { SfUnreachableBanner } from './SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.js';
import { Skeleton } from './ui/Skeleton.js';
import { Tooltip } from './ui/Tooltip.js';

import type { CroTrendResponse } from '@saledash/shared';

interface CroTrendTileProps {
  metricId: 'CRO-06' | 'CRO-07';
  data: CroTrendResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

const chipBgClass: Record<'good' | 'warn' | 'bad', string> = {
  good: 'bg-good-bg text-good-text',
  warn: 'bg-warn-bg text-warn-text',
  bad: 'bg-bad-bg text-bad-text',
};

/**
 * Build the ECharts option for the sparkline per RESEARCH.md §6.1.
 * Canvas renderer, no axes, no tooltip, brand-blue line with area fill.
 */
function buildSparklineOption(
  sparkline: number[],
  targetValue: number | null,
): object {
  // 8 weekly buckets: bucket[0] = w-7 (oldest), bucket[7] = current week.
  // Label format matches the recompute fn's bucketization.
  const weekLabels = sparkline.map((_, i) => {
    const weeksAgo = 7 - i;
    return weeksAgo === 0 ? 'This wk' : `-${String(weeksAgo)}w`;
  });

  const markLine = targetValue !== null
    ? {
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#d97706', width: 1, type: 'dashed' },
          data: [{ yAxis: targetValue, name: 'target' }],
          label: { show: true, position: 'insideEndTop', formatter: `Target ${targetValue.toFixed(1)}`, fontSize: 9, color: '#d97706' },
        },
      }
    : {};

  return {
    animation: false,
    grid: { left: 32, right: 12, top: 14, bottom: 22 },
    xAxis: {
      type: 'category',
      show: true,
      data: weekLabels,
      boundaryGap: false,
      axisLabel: { fontSize: 9, color: '#6b7280', interval: 0 },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      scale: true,
      axisLabel: { fontSize: 9, color: '#6b7280', formatter: (v: number) => v.toFixed(1) },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    },
    series: [
      {
        type: 'line',
        data: sparkline,
        smooth: true,
        lineStyle: { color: '#2851e6', width: 2 },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#2851e6' },
        areaStyle: {
          // quick-260626-cot Task 2.1: gradient area fill to match the unified
          // sparkline anatomy (denser at the line, fading to the baseline).
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(40,81,230,0.22)' },
              { offset: 1, color: 'rgba(40,81,230,0.0)' },
            ],
          },
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 9,
          color: '#1f2937',
          formatter: (p: { value: number }) => p.value.toFixed(1),
        },
        ...markLine,
      },
    ],
    tooltip: {
      show: true,
      trigger: 'axis',
      formatter: (params: { axisValue: string; value: number }[]) => {
        const p = params[0];
        if (!p) return '';
        return `${p.axisValue}<br/><strong>${p.value.toFixed(2)}</strong>`;
      },
      backgroundColor: 'rgba(17, 24, 39, 0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
    },
  };
}

/**
 * Inline WoW delta chip (quick-260626-cot Task 2.1) — replaces the stacked
 * Δw text line with a pill next to the headline value. CRO trend tiles carry
 * REAL prior-period data (prior_week_value), so the chip is always legitimate.
 */
function WoWDeltaChip({
  current,
  prior,
}: {
  current: number;
  prior: number;
}): React.JSX.Element {
  const diff = current - prior;
  const glyph = diff > 0 ? '▲' : diff < 0 ? '▼' : '—';
  const cls =
    diff > 0
      ? 'bg-good-bg text-good-text'
      : diff < 0
        ? 'bg-bad-bg text-bad-text'
        : 'bg-ink-100 text-ink-500';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${cls}`}
      data-testid="wow-delta-chip"
    >
      <span aria-hidden>{glyph}</span> {diff === 0 ? 'flat' : `${Math.abs(diff).toFixed(1)} Δw`}
    </span>
  );
}

/** Small muted Δm (vs prior-month-avg) context line. */
function MonthDeltaLine({
  current,
  prior,
}: {
  current: number;
  prior: number;
}): React.JSX.Element {
  const diff = current - prior;
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '';
  return (
    <span data-testid="month-delta">
      Δm {sign}
      {Math.abs(diff).toFixed(1)}
    </span>
  );
}

export function CroTrendTile({
  metricId,
  data,
  isLoading,
  isError,
  error,
  refetch,
}: CroTrendTileProps): React.JSX.Element {
  const definition = getCroMetricDefinition(metricId);

  const tooltipContent = (
    <>
      <p className="mb-1 font-semibold text-ink-900">{definition.name}</p>
      <p>{definition.formula_plain_english}</p>
      <p className="mt-2 text-[10px] uppercase tracking-wide text-ink-500">
        Source: {definition.source_of_truth_system}
      </p>
    </>
  );

  // UX-07: cache-warming banner
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`cro-trend-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // UX-07: SF-unreachable banner
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`cro-trend-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <SfUnreachableBanner onRetry={refetch} />
        </CardContent>
      </Card>
    );
  }

  // UX-07: loading → 3 skeletons
  if (isLoading || !data) {
    return (
      <Card data-testid={`cro-trend-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-14 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Success path

  // UI target override for CRO-06. John clarified 2026-06-11: target is 5 meetings/AE/week
  // (not the 30 currently seeded in cro_metric_target). Until the DB seed is updated, override
  // the API-returned target client-side. Recompute percent_to_target from current_value / 5.
  // CRO-07 target unchanged (6 active opps/AE).
  const TARGET_OVERRIDES: Record<string, number> = {
    'CRO-06': 5,
  };
  const targetOverride = TARGET_OVERRIDES[metricId];
  const effectiveTargetValue =
    targetOverride ?? data.target_value;
  const effectivePercentToTarget =
    targetOverride !== undefined && targetOverride !== 0
      ? data.current_value / targetOverride
      : data.percent_to_target;

  const hasTarget = effectiveTargetValue !== null && effectivePercentToTarget !== null;
  const sparklineOption = buildSparklineOption(data.sparkline, effectiveTargetValue);
  // Gap to target — for trend tiles the denominator IS effectiveTargetValue
  // (no multiplier — CRO-06/07 have target_multiplier=null). Positive gap =
  // behind; non-positive = at/over.
  const gap = effectiveTargetValue !== null ? effectiveTargetValue - data.current_value : null;

  return (
    <Card data-testid={`cro-trend-tile-${metricId}`}>
      <CardHeader>
        <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
          {definition.name}
        </CardTitle>
        <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        {/* Value row — 38px headline (text-4xl) + inline WoW delta chip.
            CRO trend tiles carry real prior_week_value, so the chip is genuine. */}
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <p
            className="text-4xl font-bold leading-none tracking-tight tabular-nums text-ink-900"
            data-testid="trend-current-value"
          >
            {formatCount(data.current_value, 1)}
          </p>
          <WoWDeltaChip
            current={data.current_value}
            prior={data.prior_week_value}
          />
        </div>

        {/* Context line — Δm (vs prior-month average). */}
        <p className="mt-2 text-xs text-ink-500">
          <MonthDeltaLine
            current={data.current_value}
            prior={data.prior_month_avg_value}
          />
        </p>

        {/* % to target chip + gap. */}
        {hasTarget && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${chipBgClass[targetChipVariant(effectivePercentToTarget)]}`}
              data-testid="percent-chip"
              aria-label={`${formatPercentToTarget(effectivePercentToTarget)} of target`}
            >
              {formatPercentToTarget(effectivePercentToTarget)} of target
            </span>
            {gap !== null && (
              <span className="text-xs text-ink-600" data-testid="gap-to-target">
                {gap > 0 ? (
                  <>Gap: <span className="font-semibold tabular-nums text-ink-900">{formatCount(gap, 1)}</span></>
                ) : (
                  <span className="font-semibold text-good-text">
                    +{formatCount(Math.abs(gap), 1)} over
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Filler zone — sparkline pinned to the bottom (mt-auto) so the card
            fills its height in equal-height grid rows. */}
        <div className="mt-auto pt-3.5">
          <div className="h-[54px] w-full" data-testid="sparkline-container">
            <ReactECharts
              option={sparklineOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              notMerge
              lazyUpdate={false}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
